import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api";
import { Loader2, Search, Printer } from "lucide-react";

export default function PrintersIndex() {
    const [printers, setPrinters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");

    useEffect(() => {
        api.get("/printers")
            .then((r) => setPrinters(r.data || []))
            .finally(() => setLoading(false));
    }, []);

    const grouped = useMemo(() => {
        const filter = search.trim().toLowerCase();
        const list = filter
            ? printers.filter((p) => p.name.toLowerCase().includes(filter))
            : printers;
        const map = {};
        for (const p of list) {
            const brand = (p.name.split(" ")[0] || "Outros").toUpperCase();
            if (!map[brand]) map[brand] = [];
            map[brand].push(p);
        }
        return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
    }, [printers, search]);

    return (
        <div className="container-edge py-10" data-testid="printers-index">
            <Helmet>
                <title>Toner & Tinteiros para todas as impressoras | SOSTONERS</title>
                <meta
                    name="description"
                    content={`Encontre consumíveis para ${printers.length} modelos de impressora — HP, Brother, Canon, Epson, Xerox, Samsung, Lexmark e mais. Compatíveis e originais com garantia 24 meses.`}
                />
                <link rel="canonical" href="https://sostoners.pt/impressoras" />
            </Helmet>

            <header className="mb-8">
                <div className="label-mono text-magenta">Catálogo SEO</div>
                <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-2 leading-tight">
                    Encontre o consumível<br className="hidden sm:block" />
                    <span className="text-magenta">para a sua impressora</span>
                </h1>
                <p className="mt-4 text-ink/70 max-w-2xl">
                    {printers.length.toLocaleString("pt-PT")} modelos de impressora indexados.
                    Toner, tinteiros, originais e compatíveis premium — com stock e envio em 24h.
                </p>
            </header>

            <div className="mb-6 max-w-xl">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" size={16} />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Procurar (ex: HP LaserJet, Brother HL-L2350)"
                        className="input-edge pl-10"
                        data-testid="printer-search-input"
                    />
                </div>
            </div>

            {loading ? (
                <div className="flex items-center gap-2 font-mono text-ink/60">
                    <Loader2 className="animate-spin" size={16} /> A carregar…
                </div>
            ) : grouped.length === 0 ? (
                <p className="font-mono text-ink/60">Sem resultados.</p>
            ) : (
                <div className="space-y-8">
                    {grouped.map(([brand, list]) => (
                        <section key={brand}>
                            <h2 className="font-display font-black text-xl mb-3 border-b-2 border-ink pb-1">
                                {brand} <span className="font-mono text-sm font-normal text-ink/50">· {list.length}</span>
                            </h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                                {list.map((p) => (
                                    <Link
                                        key={p.slug}
                                        to={`/impressora/${p.slug}`}
                                        className="border border-ink/15 hover:border-ink hover:shadow-[3px_3px_0_0_#E6007E] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-all p-3 bg-white flex items-start gap-2"
                                        data-testid={`printer-link-${p.slug}`}
                                    >
                                        <Printer size={14} className="text-ink/40 mt-1 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium truncate">{p.name}</div>
                                            <div className="text-xs font-mono text-ink/50 mt-0.5">
                                                {p.count} {p.count === 1 ? "produto" : "produtos"}
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            )}
        </div>
    );
}
